import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Mail, Eye, RefreshCw, Send, CheckCircle, Clock, 
  CreditCard, UserPlus, AlertTriangle, Gift, Trophy,
  Edit, Save, X, RotateCcw, Code
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Email template types with their metadata
const EMAIL_TEMPLATES = [
  {
    id: "welcome",
    name: "Welcome Email",
    description: "Sent when a new parent registers",
    icon: UserPlus,
    color: "emerald"
  },
  {
    id: "otp",
    name: "OTP Verification",
    description: "Sent for login and action verification",
    icon: CheckCircle,
    color: "blue"
  },
  {
    id: "trial_welcome",
    name: "Trial Started",
    description: "Sent when free trial begins",
    icon: Gift,
    color: "purple"
  },
  {
    id: "trial_expiring",
    name: "Trial Expiring Soon",
    description: "Reminder before trial ends",
    icon: Clock,
    color: "amber"
  },
  {
    id: "subscription_confirmed",
    name: "Subscription Confirmed",
    description: "Sent after successful payment",
    icon: CreditCard,
    color: "green"
  },
  {
    id: "subscription_expiring",
    name: "Subscription Expiring",
    description: "Reminder before subscription ends",
    icon: AlertTriangle,
    color: "orange"
  },
  {
    id: "weekly_progress",
    name: "Weekly Progress Report",
    description: "Weekly learning summary for parents",
    icon: Trophy,
    color: "indigo"
  }
];

export function EmailPreviewTab({ readOnly = false }) {
  const [selectedTemplate, setSelectedTemplate] = useState(EMAIL_TEMPLATES[0]);
  const [previewData, setPreviewData] = useState({ html: "", subject: "", is_custom: false });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editHtml, setEditHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCode, setShowCode] = useState(false);
  
  // Fetch current settings
  useEffect(() => {
    fetchSettings();
  }, []);
  
  // Load preview when template changes
  useEffect(() => {
    if (selectedTemplate) {
      loadPreview(selectedTemplate.id);
    }
  }, [selectedTemplate]);
  
  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/public/trial-info`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };
  
  const loadPreview = async (templateId) => {
    setLoading(true);
    setEditMode(false);
    try {
      const response = await fetch(`${API}/admin/email-preview/${templateId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setEditSubject(data.subject);
        setEditHtml(data.html);
      } else {
        toast.error("Failed to load template");
      }
    } catch (error) {
      console.error("Failed to load template:", error);
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  };
  
  const sendTestEmail = async () => {
    const email = prompt("Enter email address for test:");
    if (!email) return;
    
    try {
      const response = await fetch(`${API}/admin/email-preview/${selectedTemplate.id}/send-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(`Test email sent to ${email}`);
      } else {
        toast.error(data.detail || "Failed to send test email");
      }
    } catch (error) {
      toast.error("Failed to send test email");
    }
  };
  
  const saveTemplate = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/email-preview/${selectedTemplate.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          subject: editSubject,
          html_content: editHtml
        })
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success("Template saved successfully");
        setEditMode(false);
        loadPreview(selectedTemplate.id);
      } else {
        toast.error(data.detail || "Failed to save template");
      }
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };
  
  const resetTemplate = async () => {
    if (!confirm("Reset this template to default? This will remove all customizations.")) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/email-preview/${selectedTemplate.id}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success("Template reset to default");
        loadPreview(selectedTemplate.id);
      } else {
        toast.error(data.detail || "Failed to reset template");
      }
    } catch (error) {
      toast.error("Failed to reset template");
    }
  };
  
  const getColorClasses = (color) => {
    const colors = {
      emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
      blue: "bg-blue-100 text-blue-700 border-blue-200",
      purple: "bg-purple-100 text-purple-700 border-purple-200",
      amber: "bg-amber-100 text-amber-700 border-amber-200",
      green: "bg-green-100 text-green-700 border-green-200",
      orange: "bg-orange-100 text-orange-700 border-orange-200",
      indigo: "bg-indigo-100 text-indigo-700 border-indigo-200"
    };
    return colors[color] || colors.blue;
  };
  
  // Available placeholders for reference
  const placeholders = [
    "{{parent_name}}", "{{child_name}}", "{{student_id}}", "{{otp_code}}",
    "{{trial_days}}", "{{trial_daily_limit}}", "{{days_remaining}}",
    "{{starter_price}}", "{{plus_price}}", "{{pro_price}}",
    "{{starter_limit}}", "{{plus_limit}}", "{{pro_limit}}",
    "{{grace_period}}", "{{refund_period}}", "{{support_email}}",
    "{{sibling_discount}}", "{{annual_discount}}",
    "{{plan_name}}", "{{subjects}}", "{{amount_paid}}", "{{expiry_date}}",
    "{{accuracy}}", "{{questions_count}}", "{{streak_days}}"
  ];
  
  return (
    <div className="space-y-6">
      {/* Settings Summary */}
      {settings && (
        <Card className="bg-white border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-gray-900 text-lg">Current Dynamic Values</CardTitle>
            <CardDescription className="text-gray-500">
              These values are used in all email templates via placeholders
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Trial Period</p>
                <p className="text-gray-900 font-semibold">{settings.trial?.days} days</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Starter Price</p>
                <p className="text-gray-900 font-semibold">₹{settings.pricing?.starter?.price_per_subject}/subject</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Plus Price</p>
                <p className="text-gray-900 font-semibold">₹{settings.pricing?.plus?.price_per_subject}/subject</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Pro Price</p>
                <p className="text-gray-900 font-semibold">₹{settings.pricing?.pro?.price_per_subject}/subject</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Grace Period</p>
                <p className="text-gray-900 font-semibold">{settings.policies?.grace_period_days} days</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Refund Period</p>
                <p className="text-gray-900 font-semibold">{settings.policies?.refund_period_days} days</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Support Email</p>
                <p className="text-gray-900 font-semibold text-xs">{settings.support?.email}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Sibling Discount</p>
                <p className="text-gray-900 font-semibold">{settings.pricing?.discounts?.sibling_percent}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Template Selector */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-900 flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Templates
          </CardTitle>
          <CardDescription className="text-gray-500">
            Select a template to preview, edit, or send test emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {EMAIL_TEMPLATES.map((template) => {
              const Icon = template.icon;
              const isSelected = selectedTemplate.id === template.id;
              
              return (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    isSelected 
                      ? "border-blue-500 bg-blue-500/10" 
                      : "border-gray-300 bg-gray-50 hover:border-gray-400"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${getColorClasses(template.color)}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-gray-900 font-medium text-sm">{template.name}</p>
                  <p className="text-gray-500 text-xs mt-1">{template.description}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Email Preview / Editor */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-gray-900 flex items-center gap-2">
              {editMode ? <Edit className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              {editMode ? "Edit" : "Preview"}: {selectedTemplate.name}
              {previewData.is_custom && (
                <Badge variant="outline" className="ml-2 border-yellow-500 text-yellow-500">
                  Customized
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-gray-500">
              {editMode 
                ? "Edit the HTML template. Use placeholders like {{trial_days}} for dynamic values."
                : "This is how the email will appear to recipients"
              }
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(false)}
                  className="border-gray-300 text-gray-300 hover:bg-gray-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={saveTemplate}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save className={`h-4 w-4 mr-2 ${saving ? "animate-spin" : ""}`} />
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadPreview(selectedTemplate.id)}
                  disabled={loading}
                  className="border-gray-300 text-gray-300 hover:bg-gray-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
                {previewData.is_custom && !readOnly && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetTemplate}
                    className="border-orange-600 text-orange-400 hover:bg-orange-600/20"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                )}
                {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(true)}
                  className="border-gray-300 text-gray-300 hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                )}
                {!readOnly && (
                <Button
                  size="sm"
                  onClick={sendTestEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </Button>
                )}
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <div className="space-y-4">
              {/* Subject Line */}
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Subject Line</label>
                <Input
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  className="bg-gray-50 border-gray-300 text-gray-900"
                  placeholder="Email subject..."
                />
              </div>
              
              {/* Available Placeholders */}
              <div>
                <label className="text-sm text-gray-500 mb-2 block">Available Placeholders (click to copy)</label>
                <div className="flex flex-wrap gap-2">
                  {placeholders.map((p) => (
                    <button
                      key={p}
                      onClick={() => {
                        navigator.clipboard.writeText(p);
                        toast.success(`Copied ${p}`);
                      }}
                      className="px-2 py-1 bg-gray-50 text-gray-300 text-xs rounded hover:bg-gray-600 font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Toggle Code/Preview */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCode(!showCode)}
                  className={`border-gray-300 ${showCode ? "bg-gray-50" : ""}`}
                >
                  <Code className="h-4 w-4 mr-2" />
                  {showCode ? "Show Preview" : "Show Code"}
                </Button>
              </div>
              
              {/* HTML Editor / Live Preview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {(showCode || !showCode) && (
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">HTML Code</label>
                    <Textarea
                      value={editHtml}
                      onChange={(e) => setEditHtml(e.target.value)}
                      className="bg-gray-50 border-gray-300 text-gray-900 font-mono text-xs h-96"
                      placeholder="HTML content..."
                    />
                  </div>
                )}
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Live Preview</label>
                  <div className="bg-white rounded-lg overflow-hidden h-96 overflow-y-auto">
                    <div dangerouslySetInnerHTML={{ __html: editHtml }} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Subject Preview */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-gray-500 text-xs mb-1">Subject:</p>
                <p className="text-gray-900 font-medium">{previewData.subject}</p>
              </div>
              
              {/* Email Body Preview */}
              <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                {loading ? (
                  <div className="h-96 flex items-center justify-center">
                    <RefreshCw className="h-8 w-8 animate-spin text-gray-500" />
                  </div>
                ) : (
                  <div 
                    className="email-preview"
                    style={{ minHeight: "400px" }}
                    dangerouslySetInnerHTML={{ __html: previewData.html }}
                  />
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default EmailPreviewTab;
